import { ActionTree, ActionContext } from 'vuex';
import { RootState, useStore } from '@/store';
import { state, UserState } from './state';
import { Mutations } from './mutations';
import { UserMutationTypes } from './mutation-types';
import { UserActionTypes } from './action-types';
import { loginRequest, userInfoRequest } from '@/apis/user';
import { removeToken, setRemember, setToken } from '@/utils/cookies';
import { PermissionActionType } from '../permission/action-types';
import router, { resetRouter } from '@/router';
import { RouteRecordRaw } from 'vue-router';
import userImg from '@/assets/images/home/userImg.png';
import { ElMessage } from 'element-plus';
import i18n from '@/locales/index';
const _t = i18n.global.t;

type AugmentedActionContext = {
    commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>
} & Omit<ActionContext<UserState, RootState>, 'commit'>;

export interface Actions {
    [UserActionTypes.ACTION_LOGIN]({ commit }: AugmentedActionContext, userInfo: { username: string, password: string, company_id: string }): void
    [UserActionTypes.ACTION_RESET_TOKEN]({ commit }: AugmentedActionContext): void
    [UserActionTypes.ACTION_GET_USER_INFO]({ commit }: AugmentedActionContext): void
    [UserActionTypes.ACTION_CHANGE_ROLES]({ commit }: AugmentedActionContext, role: string): void
    [UserActionTypes.ACTION_LOGIN_OUT]({ commit }: AugmentedActionContext): void
    [UserActionTypes.ACTION_RESET_TOKEN]({ commit }: AugmentedActionContext): void
    [UserActionTypes.ACTION_LOGIN_EMAIL]({ commit }: AugmentedActionContext, info: { company_id: string, email: string, token: string, remember: boolean }): void
}

export const actions: ActionTree<UserState, RootState> & Actions = {
    async [UserActionTypes.ACTION_LOGIN]({ commit }: AugmentedActionContext, userInfo: { username: string, password: string, company_id: string }) {
        let { username, password, company_id } = userInfo;
        company_id = company_id.trim();
        username = username.toLowerCase();
        await loginRequest({ username, password, company_id })
            .then(async (res) => {
                switch (res?.code) {
                    case 200:
                        // 登录成功
                        setToken(res.data.token);
                        commit(UserMutationTypes.SET_TOKEN, res.data.token);
                        break;
                    case 400:
                        // 参数错误
                        ElMessage.closeAll();
                        ElMessage({
                            message: _t('global.error400'),
                            type: 'error',
                            duration: 1e3 * 5
                        });
                        break;
                    case 500:
                        // 服务器错误
                        ElMessage.closeAll();
                        ElMessage({
                            message: _t('global.error500'),
                            type: 'error',
                            duration: 1e3 * 5
                        });
                        break;
                    case 40001:
                        // 企业号不存在
                        ElMessage.closeAll();
                        ElMessage({
                            message: _t('system.companyNotExist'),
                            type: 'error',
                            duration: 1e3 * 5
                        });
                        break;
                    case 40002:
                        // 用户不存在
                        ElMessage.closeAll();
                        ElMessage({
                            message: _t('system.userNotExist'),
                            type: 'error',
                            duration: 1e3 * 5
                        });
                        break;
                    case 40007:
                        // 用户被禁用
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: _t('user.userDisabled')
                        });
                        break;
                    case 40031:
                        // 密码错误，锁定1次
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: _t('system.userLockedTip').replace('N', '1')
                        });
                        break;
                    case 400032:
                        // 密码错误，锁定2次
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: _t('system.userLockedTip').replace('N', '2')
                        });
                        break;
                    case 400033:
                        // 密码错误，锁定3次
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: _t('system.userLockedTip').replace('N', '3')
                        });
                        break;
                    case 400034:
                        // 密码错误，锁定4次
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: _t('system.userLockedTip').replace('N', '4')
                        });
                        break;
                    case 400035:
                        // 密码错误，锁定5次
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: _t('system.userLockedTip').replace('N', '5')
                        });
                        break;
                    default:
                        // 其他错误
                        ElMessage.closeAll();
                        ElMessage({
                            type: 'error',
                            message: res?.msg
                        });
                        break;
                }
            })
            .catch((err) => {
                console.warn(err)
                ElMessage({
                    type: 'error',
                    message: _t('system.qa.failRequeat')
                });
            });
    },

    [UserActionTypes.ACTION_RESET_TOKEN]({ commit }: AugmentedActionContext) {
        removeToken();
        commit(UserMutationTypes.SET_TOKEN, '');
        commit(UserMutationTypes.SET_ROLES, []);
    },

    async [UserActionTypes.ACTION_GET_USER_INFO]({ commit }: AugmentedActionContext) {
        if (state.token === '') {
            throw Error('token is undefined!');
        }
        await userInfoRequest().then((res: any) => {
            if (res?.code == 200) {
                const roles = res.data.username.split(); // roles支持多个角色，类型是数组，由于目前只有一个角色，所以特地转化为数组
                //commit(UserMutationTypes.SET_ROLES, roles);
                if (res.data.username == 'admin' || res.data.is_admin == 1) {
                    commit(UserMutationTypes.SET_ROLES, ['admin']);
                } else {
                    commit(UserMutationTypes.SET_ROLES, roles);
                }
                commit(UserMutationTypes.SET_NAME, res.data.username);
                commit(UserMutationTypes.SET_COMPANY, res.data.company_id);
                commit(UserMutationTypes.SET_AVATAR, userImg);

                commit(UserMutationTypes.SET_Authority, res.data.authority);
                commit(UserMutationTypes.SET_EMAIL, res.data.email);

                commit(UserMutationTypes.SET_ISADMIN, res.data.is_admin);
                commit(UserMutationTypes.SET_PWDUPDATED, res.data.pwd_updated);

                // commit(UserMutationTypes.SET_INTRODUCTION, res.data.introduction)
                // commit(UserMutationTypes.SET_EMAIL, res.data.email)
                return res;
            } else {
                // throw Error('Verification failed, please Login again.')
                router.push('/login');
            }
        });
    },

    async [UserActionTypes.ACTION_CHANGE_ROLES]({ commit }: AugmentedActionContext, role: string) {
        const token = role + '-token';
        const store = useStore();
        commit(UserMutationTypes.SET_TOKEN, token);
        setToken(token);
        await store.dispatch(UserActionTypes.ACTION_GET_USER_INFO, undefined);
        store.dispatch(PermissionActionType.ACTION_SET_ROUTES, state.roles);
        store.state.permission.dynamicRoutes.forEach((item: RouteRecordRaw) => {
            router.addRoute(item);
        });
    },

    [UserActionTypes.ACTION_LOGIN_OUT]({ commit }: AugmentedActionContext) {
        console.log(commit);
        removeToken();
        commit(UserMutationTypes.SET_TOKEN, '');
        commit(UserMutationTypes.SET_ROLES, []);
        resetRouter();
    },

    async [UserActionTypes.ACTION_LOGIN_EMAIL]({ commit }: AugmentedActionContext, info: { company_id: string, email: string, token: string, remember: boolean }) {
        if (info.remember) setRemember(info.company_id, info.email);
        setToken(info.token);
        commit(UserMutationTypes.SET_TOKEN, info.token);
    }
};
